import {
  Box,
  BoxProps,
  Stack,
  Typography
} from "@mui/material";
import { common } from "@mui/material/colors";
import {
  alpha,
  styled
} from "@mui/material/styles";
import {
  ButtonBookmark,
  ButtonLink,
  ButtonProps,
  Icon
} from "../../components";

import { ButtonDigited } from "../../components/Button/Button.variants";
import {
  StyledStatusBox
} from "../../components/StatusBox/StatusBox.component";
import { CtaOverlayContainer } from "../CtaOverlay/CtaOverlay.styles";
import { PlayerLoader } from "../PlayerLoader";
import type { PlayerLoaderProps } from "components/PlayerLoader/PlayerLoader.types";

export const StyledButtonBookmark = styled(ButtonBookmark)<
  ButtonProps & { $selected: boolean, $isFromYoutube: boolean }
>(({ $selected, $isFromYoutube, theme }) => ({
  alignItems: "center",
  border: "none",
  bottom: $isFromYoutube ? "100px" : "65px",
  cursor: "pointer",
  display: "flex",
  height: "30px",
  minWidth:"30px",
  opacity: 0,
  padding: 0,
  position: "absolute",
  right: "100px",
  transition: "all .1s ease 0s",
  zIndex: "3 !important",

  "&:before": {
    /* eslint-disable max-len */
    backgroundImage: `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='38'%20height='38'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23${($selected ? theme.customColors.systemSecondary01 : theme.customColors.textPrimary).replace("#", "")}'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpath%20d='M13%2013.74a2%202%200%200%201-2%200L2.5%208.87a1%201%200%200%201%200-1.74L11%202.26a2%202%200%200%201%202%200l8.5%204.87a1%201%200%200%201%200%201.74z'/%3E%3Cpath%20d='m20%2014.285%201.5.845a1%201%200%200%201%200%201.74L13%2021.74a2%202%200%200%201-2%200l-8.5-4.87a1%201%200%200%201%200-1.74l1.5-.845'/%3E%3C/svg%3E")`,
    /* eslint-enable max-len */
    backgroundRepeat: "no-repeat",
    content: " '' !important ",
    display: "flex",
    height: "40px",
    minWidth: "40px"
  },
  "&:hover":{
    transform: "scale(1.2)",
    backgroundColor: "transparent !important"
  },

  [theme.breakpoints.down("sm")]: {
    bottom:"10px",
    right: "20px",

    "&:before":{
      backgroundSize:"contain",
      height: "18px",
      minWidth:"18px"
    }
  }
}));

export const StyledButtonLink = styled(ButtonLink)(({ theme }) => ({
  minWidth:"unset",
  opacity: 0,
  pointerEvents: "none",
  color: theme.customColors.backgroundPrimaryCta,
  fontSize:"1.141rem",
  fontWeight:"400",
  gap:"9px",
  zIndex: "1",
  position:"absolute",
  border: `1px solid ${theme.customColors.backgroundPrimaryCta}`,
  backgroundColor: alpha(theme.customColors.textPrimaryCta, 0.8),
  "&.MuiButton-outlinedPrimary": {
    "&:hover": {
      "borderColor": theme.customColors.backgroundPrimaryCta
    }
  },

  left:"42px",
  top:"32px",

  ".icon":{
    height:"18px",
    width:"13px"
  },

  [theme.breakpoints.down("sm")]: {
    left: "20px",
    minWidth: "auto",
    top: "16px"
  }
}));
export const ButtonDigitedLink = styled(ButtonDigited)(({ theme }) => ({
  pointerEvents: "none",
  gap:"0.4rem",
  zIndex: "1",
  position:"absolute",
  left:"3rem",
  top:"1.5rem",
  [theme.breakpoints.down("sm")]: {
    left: "1.25rem",
    top: "1.5rem",
    fontSize: "0.8125rem"
  }
}));

export const StyledIcon = styled(Icon)(({ theme }) => ({
  "path":{
    fill: theme.customColors.textPrimary
  }
}));

export const StyledCoverLoader = styled(Box)<
  BoxProps & {
    $cover?: string
    $videoVisible: boolean
    $showGradient: boolean
  }
>(({
  theme,
  $cover,
  $videoVisible,
  $showGradient
}) => ({
  background: `${theme.linearGradients.gradientDAlternative01},
  ${theme.linearGradients.gradientDAlternative03},
  url(${$cover})`,
  backgroundPosition: "center",
  backgroundRepeat:"no-repeat",
  backgroundSize: "cover",
  justifyContent:"flex-end",
  minHeight: "750px",
  overflow: "hidden",
  width: "100%",

  ">main":{
    pointerEvents:"none",
    zIndex: 1
  },

  "> main >span":{
    opacity:!$videoVisible ? 1 : 0,
    right:"42px"
  },

  [`${StyledButtonBookmark}, ${StyledButtonLink}, ${StyledStatusBoxPlayer}, #completed-tag-brightcove, .back-button`]:{
    opacity: !$videoVisible ? 1 : 0,
    pointerEvents: !$videoVisible ? "auto" : "none"
  },

  [`${ButtonDigitedLink}`]: {
    opacity: !$videoVisible ? 1 : 0,
    pointerEvents: !$videoVisible ? "auto" : "none"
  },

  [".back-button"]: {
    opacity: !$videoVisible ? 1 : 0,
    pointerEvents: !$videoVisible ? "auto" : "none"
  },

  ".video-caption": {
    height: "fit-content",
    opacity: !$videoVisible ? 1 : 0,
    pointerEvents: "none"
  },

  ".icon-play":{
    alignItems:"center",
    backgroundColor: theme.customColors.backgroundPrimary,
    border:`1px solid ${theme.customColors.textPrimaryCta}`,
    borderRadius:"50%",
    cursor: "pointer",
    display:"flex",
    height:"70px",
    justifyContent: "center",
    margin:"auto",
    position:"absolute",
    width:"70px",

    bottom:0,
    left:0,
    right:0,
    top:0,

    "& path": {
      fill: theme.customColors.textPrimaryCta
    }
  },

  "&:hover":{
    [`${StyledButtonBookmark}, ${StyledButtonLink}, ${StyledStatusBoxPlayer}, > main >span, .video-caption, #completed-tag-brightcove, .back-button`]:{
      opacity: $showGradient ? 1 : 0,
      pointerEvents:"auto",
      zIndex:theme.zIndex.drawer
    },
    [`${StyledButtonBookmark}, ${ButtonDigitedLink}, ${StyledStatusBoxPlayer}, > main >span, .video-caption, #completed-tag-brightcove, .back-button`]:{
      opacity: $showGradient ? 1 : 0,
      display:"unset",
      pointerEvents:"auto",
      zIndex:theme.zIndex.drawer
    }
  },

  [theme.breakpoints.down("sm")]: {
    minHeight: "450px",

    [`${StyledButtonLink}`]:{
      fontSize: "0.75rem",

      [`${StyledIcon}`]:{
        height: "15px",
        width: "12px"
      }
    },
    [`${ButtonDigitedLink}`]:{
      fontSize: "0.75rem",

      [`${StyledIcon}`]:{
        height: "15px",
        width: "12px"
      }
    },

    ".video-caption":{
      "h3":{
        fontSize: "1rem"
      }
    }
  }
}));

export const StyledPlayerLoader = styled(PlayerLoader)<
  PlayerLoaderProps
>(({ theme, isMobile, isLandscape, isFullScreen, $gradientShow }) => ({
  overflow: "hidden",
  position:"relative",

  "&::before":{
    backgroundImage: `${theme.linearGradients.gradientVideoPlayerLoader}`,
    content:"''",
    inset:0,
    opacity:1,
    position:"absolute",
    transition:"all .3s linear",
    zIndex:0
  },

  height: "100%",
  width: "100%",

  ">main >span":{
    right:"42px"
  },

  [`${StyledButtonBookmark}`]:{
    right:"85px"
  },

  ".video-caption": {
    height: "fit-content",
    pointerEvents: "none"
  },

  ".video-js": {
    display:"block",
    height: "100%",
    margin: "0 auto",
    maxWidth: "1440px",
    padding: "0",
    width: "100%",

    "video": {
      objectFit: "contain"
    },

    "span": {
      "&:before":{
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        position: "static"
      }
    },

    ".vjs-big-play-button": {
      alignItems: "center",
      backgroundColor: theme.customColors.backgroundPrimary,
      border: `1px solid ${theme.customColors.backgroundPrimary}`,
      borderRadius: "50%",
      display: "flex",
      justifyContent: "center",

      height: "70px",
      width: "70px",
      zIndex:1,

      "span":{
        "&:before":{
          //eslint-disable-next-line max-len
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23${theme.customColors.backgroundPrimaryCta.replace("#", "")}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z'/%3E%3C/svg%3E")`,
          color: theme.customColors.textPrimaryCta,
          content: " '' !important ",
          display: "flex",
          position: "static",

          height: "1.5rem",
          width: "1.5rem"
        },
        height: "1.5rem",
        width: "1.5rem"
      }

    },

    ".vjs-control-bar": {
      alignItems: "center",
      background: `${(isMobile || isFullScreen) ? `linear-gradient(0deg, rgba(${theme.customColors.gradient02Start}) 10%,
   rgba(${theme.customColors.gradient02End}) 100%), linear-gradient(180deg, rgba(${theme.customColors.gradient02Start}) 10%,
   rgba(${theme.customColors.gradient02End}) 100%)` : "transparent"}`,
      bottom: `${(isLandscape && isFullScreen) ? "10px" : "40px"}`,
      flexWrap: "wrap",
      height: "5rem",
      margin: "0 auto ",
      width: "95%",
      maxWidth: "1440px",
      padding: `${(isFullScreen) ? "20px 42px 0px 42px" : "0 42px"}`,
      transform: "translateY(0) !important",
      borderRadius: `${(isFullScreen) ? "10px" : "0"}`,
      zIndex: "2 !important",

      ".vjs-playback-rate, .vjs-subs-caps-button":{
        right: (isFullScreen ? "0" : "45px"),
        ">.vjs-menu":{
          ".vjs-menu-content":{
            backgroundColor: alpha(theme.customColors.backgroundPrimaryCta,0.3),
            borderRadius:"0.24rem",

            ".vjs-menu-item":{
              backgroundColor:alpha(theme.customColors.backgroundPrimaryLighter,0.7),
              color: theme.customColors.systemPrimary01,

              "&:hover":{
                // eslint-disable-next-line max-len
                textShadow:`0 0 1em ${theme.customColors.backgroundPrimaryCta}, 0 0 1em ${theme.customColors.backgroundPrimaryCta}, 0 0 1em ${theme.customColors.backgroundPrimaryCta}`
              },

              "&.vjs-selected":{
                backgroundColor:theme.customColors.backgroundPrimaryCta,
                color:theme.customColors.textPrimaryAlternative
              }
            }
          }
        },
        [theme.breakpoints.down("sm")]: {
          right: (isFullScreen ? "0" : "35px")
        }
      },


      ".vjs-playback-rate":{
        color: theme.customColors.textPrimary,
        height:"40px",
        width:"30px",

        ".vjs-playback-rate-value":{
          height:"fit-content",
          margin:"auto",

          bottom:0,
          rigth:0
        }
      },

      ".vjs-current-time ": {
        marginLeft: "8px"
      },
      ".vjs-current-time, .vjs-duration, .vjs-time-divider ": {
        fontSize: "1rem",
        margin: "0",
        padding: "0",

        "span":{
          color:theme.customColors.textPrimary
        }
      },
      ".vjs-fullscreen-control": {
        right: (isFullScreen ? "0" : "45px"),
        order: "9999",
        "span":{
          "&:before":{
            //eslint-disable-next-line max-len
            backgroundImage: `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23${theme.customColors.textPrimary.replace("#", "")}'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpath%20d='M8%203H5a2%202%200%200%200-2%202v3'/%3E%3Cpath%20d='M21%208V5a2%202%200%200%200-2-2h-3'/%3E%3Cpath%20d='M3%2016v3a2%202%200%200%200%202%202h3'/%3E%3Cpath%20d='M16%2021h3a2%202%200%200%200%202-2v-3'/%3E%3C/svg%3E")`
          }
        },
        [theme.breakpoints.down("sm")]: {
          right: (isFullScreen ? "0" : "35px")
        }
      },
      ".vjs-picture-in-picture-control": {
        display: "none"
      },
      ".vjs-play-control": {
        "span":{

          "&:before": {
            // eslint-disable-next-line max-len
            backgroundImage: `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23${theme.customColors.textPrimary.replace("#", "")}'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Crect%20x='14'%20y='3'%20width='5'%20height='18'%20rx='1'/%3E%3Crect%20x='5'%20y='3'%20width='5'%20height='18'%20rx='1'/%3E%3C/svg%3E")`
          }
        },

        "&.vjs-paused":{
          "span":{
            "&:before":{
              //eslint-disable-next-line max-len
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23${theme.customColors.textPrimary.replace("#", "")}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z'/%3E%3C/svg%3E")`
            }
          }
        }
      },



      ".vjs-progress-control": {
        margin: "0",
        order: "0",
        width: "100%",
        height: "7px!important",

        maxWidth: `${(!isMobile && isFullScreen) ? "100%" : "1440px"}`,
        padding:"0 42px",
        top: `${(!isMobile && isFullScreen) ? "15px !important" : "0 !important"}`,


        ".vjs-load-progress":{
          borderRadius: "40px",

          ">div": {
            background: alpha(theme.customColors.textWidget,0.4),
            borderRadius: "40px"
          }
        },

        ":hover .vjs-progress-holder": {
          height: "7px",
          fontSize: "inherit"
        },

        // "&:hover ": {

        //   ".vjs-progress-holder .vjs-play-progress": {
        //     "&:before": {
        //       bottom: "-0.9em",
        //       height: "30px",
        //       width: "30px"
        //     }
        //   }

        // },

        ".vjs-play-progress": {
          backgroundColor: theme.customColors.accentVariantB,
          borderRadius: "40px",
          position: "relative",

          "&:before": {
            backgroundColor: theme.customColors.accentVariantB,
            border: `2px solid ${theme.customColors.borderTag}`,
            borderRadius: "40px",
            bottom: "-6px",
            content: "''",
            display: "flex",
            height: "24px",
            width: "24px",
            zIndex:0,

            position: "absolute"
          }

        },

        ".vjs-progress-holder": {
          backgroundColor: alpha(theme.customColors.textWidget,0.4),
          borderRadius: "40px",
          height: "7px",
          margin: "0"
        },

        ".vjs-time-tooltip": {
          top: "-5.4em"
        }
      },
      ".vjs-subs-caps-button": {
        alignItems: "center",
        cursor: "pointer",
        display: "flex !important",
        order: "9999",
        padding: "0",

        ">span": {
          "&:before": {
            //eslint-disable-next-line max-len
            backgroundImage: `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23${theme.customColors.textPrimary.replace("#", "")}'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Crect%20width='18'%20height='14'%20x='3'%20y='5'%20rx='2'%20ry='2'/%3E%3Cpath%20d='M7%2015h4M15%2015h2M7%2011h2M13%2011h4'/%3E%3C/svg%3E")`
          }
        },

        ".vjs-menu ":{
          ".vjs-menu-content":{
            bottom: "3.5em",
            zIndex: 4,

            // hide captions setting button
            ".vjs-texttrack-settings":{
              display:"none"
            }
          }
        }
      },
      ".vjs-time-divider ": {
        minWidth: "unset",
        width: "10px"
      },
      ".vjs-volume-panel ": {
        marginRight: "0",


        ".vjs-mute-control":{

          height:"fit-content",

          "span":{
            "&:before":{
              //eslint-disable-next-line max-len
              backgroundImage: `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23${theme.customColors.textPrimary.replace("#", "")}'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpath%20d='M11%204.702a.705.705%200%200%200-1.203-.498L6.413%207.587A1.4%201.4%200%200%201%205.416%208H3a1%201%200%200%200-1%201v6a1%201%200%200%200%201%201h2.416a1.4%201.4%200%200%201%20.997.413l3.383%203.384A.705.705%200%200%200%2011%2019.298z'/%3E%3Cpath%20d='M16%209a5%205%200%200%201%200%206'/%3E%3Cpath%20d='M19.364%2018.364a9%209%200%200%200%200-12.728'/%3E%3C/svg%3E")`,
              backgroundPosition:"center"
            }

          },

          "&.vjs-vol-0":{
            "span":{
              "&:before":{
                //eslint-disable-next-line max-len
                backgroundImage: `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23${theme.customColors.textPrimary.replace("#", "")}'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpath%20d='M16%209a5%205%200%200%201%20.95%202.293'/%3E%3Cpath%20d='M19.364%205.636a9%209%200%200%201%201.889%209.96'/%3E%3Cpath%20d='m2%202%2020%2020'/%3E%3Cpath%20d='m7%207-.587.587A1.4%201.4%200%200%201%205.416%208H3a1%201%200%200%200-1%201v6a1%201%200%200%200%201%201h2.416a1.4%201.4%200%200%201%20.997.413l3.383%203.384A.705.705%200%200%200%2011%2019.298V11'/%3E%3Cpath%20d='M9.828%204.172A.686.686%200%200%201%2011%204.657v.686'/%3E%3C/svg%3E")`
              }
            }
          }


        },

        ".vjs-volume-control":{
          borderRadius: "4px",
          height: "5px",
          ".vjs-volume-bar":{
            backgroundColor: alpha(theme.customColors.textWidget,0.4)
          },

          ".vjs-volume-bar.vjs-slider-horizontal":{
            borderRadius: "4px",
            height: "5px"
          }
        },

        ".vjs-volume-level": {
          backgroundColor: theme.customColors.accentVariantB,
          borderRadius: "4px",
          height: "5px"
        }
      },
      ".vjs-volume-panel .vjs-button, .vjs-play-control, .vjs-fullscreen-control, .vjs-subs-caps-button": {
        outline: "none",
        "&:hover": {
          transform: "scale(1.2)"
        },
        "> span":{
          "&:before":{
            content: "'' !important",
            display: "flex",
            height: "24px",
            width: "24px"
          },
          height: "24px",
          width: "24px"
        },
        transition: "all .3s ease"
      },
      "> div, > button": {
        alignItems: "center",
        margin: "0 0.5rem",
        order: "1",
        width: "fit-content"
      }
    },

    ".vjs-poster": {
      "img": {
        objectFit: "cover"
      }
    },

    "picture.vjs-poster::after": {
      content: "\"\"",
      position: "absolute",
      inset: 0,
      background: theme.linearGradients.gradientC,
      zIndex: 1,
      pointerEvents: "none"
    },

    ".vjs-title-bar": {
      display: "none"
    }
  },

  "> div": {
    height: "100%"
  },
  ">main":{
    pointerEvents:"none",
    zIndex: 1
  },

  [` >main >span, .video-caption, #completed-tag-brightcove, ${StyledButtonBookmark}, ${StyledStatusBoxPlayer}`]: {
    opacity: 0,
    pointerEvents: "none",
    transition: "all .3s linear"
  },

  "&.brightcove-is-paused": {
    [`>main >span, ${ButtonDigitedLink}, ${StyledButtonLink}, ${StyledButtonBookmark}`]: {
      pointerEvents: "auto"
    },
    [`>main >span, .video-caption,
    ${ButtonDigitedLink}`]:{
      display:"flex"
    },
    [`>main >span, .video-caption, #completed-tag-brightcove,
    ${StyledButtonLink},
    ${StyledButtonBookmark},
    ${StyledButtonBookmark},
    ${StyledStatusBoxPlayer}`]: {
      opacity: 1
    },
    [`${ButtonDigitedLink}`]: {
      opacity: "1 !important",
      pointerEvents: "auto"
    },
    "&::before":{
      opacity:1,
      zIndex:1
    }
  },

  "&.brightcove-is-playing":{

    ".vjs-big-play-button": {
      display: "none"
    },

    ".vjs-control-bar": {
      opacity: "0 !important"
    },

    "&::before":{
      opacity: 0,
      zIndex:0
    },

    [`${ButtonDigitedLink}`]: {
      opacity: 0,
      pointerEvents: "none"
    },

    "&:hover":{
      [` ${StyledButtonBookmark}, ${StyledStatusBoxPlayer}, #completed-tag-brightcove, .back-button , .vjs-control-bar, .video-caption`]: {
        opacity: $gradientShow ? "1 !important" : 0,
        pointerEvents: "auto"
      },

      "&::before":{
        opacity: $gradientShow ? 1 : 0,
        zIndex:1
      }
    }


  },

  "&.isRestricted":{

    [`${StyledButtonBookmark}`]:{
      right:"85px",

      [theme.breakpoints.down("md")]: {
        right: "70px"
      }
    },
    ".video-js": {

      ".vjs-control-bar": {
        ".vjs-progress-control":{
          opacity:0.5,
          pointerEvents:"none"
        },

        ".vjs-playback-rate":{
          display:"none"
        }

      }
    }
  },

  "&:hover": {
    [`> main >span,  ${ButtonDigitedLink}, ${StyledButtonLink}`]: {
      pointerEvents: "auto"
    },
    [`> main >span, .video-caption, ${ButtonDigitedLink}, ${StyledButtonLink}, ${StyledStatusBoxPlayer}`]: {
      opacity: 1
    }
  },
  [theme.breakpoints.down("lg")]: {
    ".video-caption":{
      paddingBottom: "30px !important"
    }

  },

  [theme.breakpoints.up(1920)]: {
    ".vjs-text-track-cue": {
      inset: `${isLandscape && isFullScreen ? "75vh 0px 0px !important" : "inherit"}`
    }
  },

  [theme.breakpoints.down(1200)]: {
    ".vjs-text-track-cue": {
      inset: `${isLandscape && isFullScreen ? "65vh 0px 0px !important" : "inherit"}`
    }
  },

  [theme.breakpoints.down("sm")]: {

    [`${StyledButtonLink}, ${ButtonDigitedLink}`]:{
      fontSize: "0.5rem",

      [`${StyledIcon}`]:{
        height: "15px",
        width: "12px"
      }
    },
    [`${StyledButtonBookmark}`]:{
      right:"40px"
    },
    ".video-js":{
      position: "relative",

      "video":{
        position: "relative"
      },

      ".vjs-control-bar":{
        bottom: "0px",
        height: "3rem",
        padding:"0 20px",

        ".vjs-progress-control":{
          padding:"0 20px",
          top: "-1.8em"
        },

        ".vjs-playback-rate":{
          display:"flex !important",
          height: "18px",
          width: "18px",

          ">.vjs-menu":{
            ".vjs-menu-content":{
              bottom:"0px !important"
            }

          }
        },

        ".vjs-volume-panel .vjs-button, .vjs-play-control, .vjs-fullscreen-control, .vjs-subs-caps-button":{
          "> span":{
            "&:before":{
              height: "18px",
              width: "18px"
            },
            height: "18px",
            width: "18px"
          }
        }
      },

      ".vjs-big-play-button":{
        border: "none"
      }

    },

    ".video-caption":{

      paddingBottom: "25px !important",

      "h3":{
        fontSize: "0.75rem"
      }
    },

    "&.isRestricted":{
      ".video-js": {
        ".vjs-control-bar": {
          ".vjs-playback-rate":{
            display:"none !important"
          }
        }
      },


      [`${StyledButtonBookmark}`]:{
        right:"85px"
      },

      [theme.breakpoints.down("sm")]: {
        [`${StyledButtonBookmark}`]:{
          right:"35px"
        }
      }
    }
  }
}));

export const StyledStatusBoxPlayer = styled(Box)<BoxProps>(({ theme }) => ({

  [`${StyledStatusBox}`]:{
    left: "0px",
    marginBottom: "42px",
    top: "16%",
    zIndex: theme.zIndex.appBar,

    height: "60px",
    width: "60px",

    "svg":{
      height:"30px !important",
      width:"30px !important"
    },

    [theme.breakpoints.down("sm")]: {
      height: "40px",
      top:"20%",
      width: "40px",

      [`${StyledIcon}`]:{
        height: "24px",
        width: "24px"
      }
    }
  }

}));

export const VideoStack = styled(Stack)<{$isExternal: boolean}>(({ theme, $isExternal }) => ({
  background: theme.palette.background?.paper,
  minHeight: $isExternal ? "750px" : "100%",

  [theme.breakpoints.down("sm")]:{

    height: $isExternal ? "450px" : "100%",
    minHeight:$isExternal ? "450px" : "100%",

    [`${CtaOverlayContainer}`]: {
      display: "none"
    }
  }
}));

export const TitleTypography = styled(Typography)(({ theme }) => ({
  fontSize: "3rem",
  lineHeight: 7 / 6,
  textShadow: `0 0 1.25rem ${alpha(theme.palette.background?.paper ?? common["black"], 0.25)}`
}));

export const StyledScormPlayButton = styled(Box)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.customColors.backgroundPrimary,
  border: `1px solid ${theme.customColors.backgroundPrimary}`,
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",

  height: "70px",
  width: "70px",
  zIndex:1,

  "span":{
    "&:before":{
      //eslint-disable-next-line max-len
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='21' height='26' viewBox='0 0 21 26' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.09943 24.0796C3.44445 25.2429 1.16729 24.0421 1.17974 22.0157L1.24196 12.0212L1.24196 3.29003C1.24196 1.16353 3.74309 0.000217722 5.36074 1.38869L18.9614 10.345C20.2929 11.3832 20.2431 13.4096 18.8619 14.3728L9.15599 21.2151' stroke='%23${theme.customColors.backgroundPrimaryCta.replace("#", "")}' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");`,
      color: theme.customColors.backgroundPrimaryCta,
      content: " '' !important ",
      display: "flex",
      position: "static",

      height: "26px",
      width: "21px"
    },
    height: "26px",
    width: "21px"
  }
}));


